import { useMemo } from 'react';

import type { CalculatedColumn, Column, ColumnMetric, FormatterProps } from '../types';
import type { DataGridProps } from '../types';
import { ValueFormatter, ToggleGroupFormatter } from '../formatters';
import { SELECT_COLUMN_KEY } from '../Columns';
import ReadOnlyEditor from '../editors/ReadOnlyEditor';

interface CalculatedColumnsArgs<R, SR> extends Pick<DataGridProps<R, SR>, 'defaultColumnOptions'> {
  rawColumns: readonly Column<R, SR>[];
  rawGroupBy?: readonly string[];
  viewportWidth: number;
  scrollLeft: number;
  columnWidths: ReadonlyMap<string, number>;
  enableVirtualization: boolean;
  rowsCount: number;
  readonly?: boolean;
}

export function useCalculatedColumns<R, SR>({
  rawColumns,
  columnWidths,
  viewportWidth,
  scrollLeft,
  defaultColumnOptions,
  rawGroupBy,
  enableVirtualization,
  rowsCount,
  readonly,
}: CalculatedColumnsArgs<R, SR>) {
  const minColumnWidth = defaultColumnOptions?.minWidth ?? 80;
  const defaultFormatter = defaultColumnOptions?.formatter ?? ValueFormatter;
  const defaultSortable = defaultColumnOptions?.sortable ?? true;
  const defaultResizable = defaultColumnOptions?.resizable ?? false;

  const selectColumnWidth = Math.floor(
    Math.floor(Math.log(rowsCount || 1) / Math.log(10)) * 10 + 35,
  );

  const { columns, lastFrozenColumnIndex, groupBy } = useMemo(() => {
    // Filter rawGroupBy and ignore keys that do not match the columns prop
    const groupBy: string[] = [];
    let lastFrozenColumnIndex = -1;

    const columns = rawColumns.map((rawColumn) => {
      const rowGroup = rawGroupBy?.includes(rawColumn.key) ?? false;
      // eslint-disable-next-line
      const frozen = rowGroup || rawColumn.frozen || false;

      const column: CalculatedColumn<R, SR> = {
        ...rawColumn,
        idx: 0,
        frozen,
        isLastFrozenColumn: false,
        rowGroup,
        sortable: rawColumn.sortable ?? defaultSortable,
        resizable: rawColumn.resizable ?? defaultResizable,
        clickable: rawColumn.clickable ?? true,
        formatter:
          rawColumn.formatter ?? (defaultFormatter as React.ComponentType<FormatterProps<R, SR>>),
        editor: readonly ? ReadOnlyEditor : rawColumn.editor,
      };

      if (rawColumn.key === SELECT_COLUMN_KEY) {
        column.width = selectColumnWidth;
      }
      if (rowGroup) {
        column.groupFormatter ??= ToggleGroupFormatter;
      }

      if (frozen) {
        lastFrozenColumnIndex++;
      }

      return column;
    });

    columns.sort(({ key: aKey, frozen: frozenA }, { key: bKey, frozen: frozenB }) => {
      // Sort select column first:
      if (aKey === SELECT_COLUMN_KEY) {
        return -1;
      }
      if (bKey === SELECT_COLUMN_KEY) {
        return 1;
      }

      // Sort grouped columns second, following the groupBy order:
      if (rawGroupBy?.includes(aKey)) {
        if (rawGroupBy.includes(bKey)) {
          return rawGroupBy.indexOf(aKey) - rawGroupBy.indexOf(bKey);
        }
        return -1;
      }
      if (rawGroupBy?.includes(bKey)) {
        return 1;
      }

      // Sort frozen columns third:
      if (frozenA) {
        if (frozenB) {
          return 0;
        }
        return -1;
      }
      if (frozenB) {
        return 1;
      }

      // Sort other columns last:
      return 0;
    });

    columns.forEach((column, idx) => {
      column.idx = idx;
    });

    if (lastFrozenColumnIndex !== -1) {
      columns[lastFrozenColumnIndex].isLastFrozenColumn = true;
    }

    return {
      columns,
      lastFrozenColumnIndex,
      groupBy,
    };
  }, [
    rawColumns,
    defaultFormatter,
    defaultResizable,
    defaultSortable,
    rawGroupBy,
    rowsCount,
    readonly,
  ]);

  const { layoutCssVars, totalColumnWidth, totalFrozenColumnWidth, columnMetrics } = useMemo(() => {
    const columnMetrics = new Map<CalculatedColumn<R, SR>, ColumnMetric>();
    let left = 0;
    let totalColumnWidth = 0;
    let totalFrozenColumnWidth = 0;
    let templateColumns = '';
    let allocatedWidth = 0;
    let unassignedColumnsCount = 0;

    // 根据 column 配置指定的 column.width 计算列宽
    for (const column of columns) {
      let width = getSpecifiedWidth(column, columnWidths, viewportWidth);

      if (width === undefined) {
        unassignedColumnsCount++;
      } else {
        width = clampColumnWidth(width, column, minColumnWidth);
        allocatedWidth += width;
        columnMetrics.set(column, { width, left: 0 });
      }
    }
    /**
     * 多-1来为了防止出现抖动的bug
     */
    const unallocatedWidth = viewportWidth - allocatedWidth - 1;
    const floorWidth = Math.floor(unallocatedWidth / unassignedColumnsCount);
    const lastValue = Math.floor(unallocatedWidth - floorWidth * (unassignedColumnsCount - 1));
    let unallocatedColumnWidth = [];
    if (unassignedColumnsCount > 0) {
      unallocatedColumnWidth = new Array(unassignedColumnsCount - 1).fill(floorWidth);
      unallocatedColumnWidth.push(lastValue);
    }

    // 计算未指定 column.width 的列宽和 column.left
    for (const column of columns) {
      let width: number;
      if (columnMetrics.has(column)) {
        const columnMetric = columnMetrics.get(column) as ColumnMetric;
        columnMetric.left = left;
        ({ width } = columnMetric);
      } else {
        width = clampColumnWidth(unallocatedColumnWidth.pop(), column, minColumnWidth);
        columnMetrics.set(column, { width, left });
      }
      totalColumnWidth += width;
      left += width;
      templateColumns += `${width}px `;
    }

    if (lastFrozenColumnIndex !== -1) {
      const columnMetric = columnMetrics.get(columns[lastFrozenColumnIndex]) as ColumnMetric;
      totalFrozenColumnWidth = columnMetric.left + columnMetric.width;
    }

    const layoutCssVars: Record<string, string> = {
      '--template-columns': templateColumns,
    };

    for (let i = 0; i <= lastFrozenColumnIndex; i++) {
      const column = columns[i];
      layoutCssVars[`--frozen-left-${column.key}`] = `${columnMetrics.get(column)?.left ?? 0}px`;
    }

    return { layoutCssVars, totalColumnWidth, totalFrozenColumnWidth, columnMetrics };
  }, [columnWidths, columns, viewportWidth, minColumnWidth, lastFrozenColumnIndex]);

  const [colOverscanStartIdx, colOverscanEndIdx, colVisibleStartIdx, colVisibleEndIdx] =
    useMemo((): [number, number] | [number, number, number, number] => {
      if (!enableVirtualization) {
        return [0, columns.length - 1];
      }
      // get the viewport's left side and right side positions for non-frozen columns
      const viewportLeft = scrollLeft + totalFrozenColumnWidth;
      const viewportRight = scrollLeft + viewportWidth;
      // get first and last non-frozen column indexes
      const lastColIdx = columns.length - 1;
      const firstUnfrozenColumnIdx = Math.min(lastFrozenColumnIndex + 1, lastColIdx);

      // skip rendering non-frozen columns if the frozen columns cover the entire viewport
      if (viewportLeft >= viewportRight) {
        return [firstUnfrozenColumnIdx, firstUnfrozenColumnIdx];
      }

      // get the first visible non-frozen column index
      let colVisibleStartIdx = firstUnfrozenColumnIdx;
      while (colVisibleStartIdx < lastColIdx) {
        const { left, width } = columnMetrics.get(columns[colVisibleStartIdx]) as ColumnMetric;
        // if the right side of the columnn is beyond the left side of the available viewport,
        // then it is the first column that's at least partially visible
        if (left + width > viewportLeft) {
          break;
        }
        colVisibleStartIdx++;
      }

      // get the last visible non-frozen column index
      let colVisibleEndIdx = colVisibleStartIdx;
      while (colVisibleEndIdx < lastColIdx) {
        const { left, width } = columnMetrics.get(columns[colVisibleEndIdx]) as ColumnMetric;
        // if the right side of the column is beyond or equal to the right side of the available viewport,
        // then it the last column that's at least partially visible,
        // as the previous column's right side is not beyond the viewport.
        if (left + width >= viewportRight) {
          break;
        }
        colVisibleEndIdx++;
      }

      const colOverscanStartIdx = Math.max(firstUnfrozenColumnIdx, colVisibleStartIdx - 10);
      const colOverscanEndIdx = Math.min(lastColIdx, colVisibleEndIdx + 10);

      return [colOverscanStartIdx, colOverscanEndIdx, colVisibleStartIdx, colVisibleEndIdx];
    }, [
      columnMetrics,
      columns,
      lastFrozenColumnIndex,
      scrollLeft,
      totalFrozenColumnWidth,
      viewportWidth,
      enableVirtualization,
    ]);

  return {
    columns,
    colOverscanStartIdx,
    colOverscanEndIdx,
    layoutCssVars,
    columnMetrics,
    totalColumnWidth,
    lastFrozenColumnIndex,
    totalFrozenColumnWidth,
    groupBy,
    colVisibleStartIdx,
    colVisibleEndIdx,
  };
}

function getSpecifiedWidth<R, SR>(
  { key, width }: Column<R, SR>,
  columnWidths: ReadonlyMap<string, number>,
  viewportWidth: number,
): number | undefined {
  if (columnWidths.has(key)) {
    // Use the resized width if available
    return columnWidths.get(key);
  }
  if (typeof width === 'number') {
    return width;
  }
  if (typeof width === 'string' && /^\d+%$/.test(width)) {
    return Math.floor((viewportWidth * parseInt(width, 10)) / 100);
  }
  return undefined;
}

function clampColumnWidth<R, SR>(
  width: number,
  { minWidth, maxWidth, key }: Column<R, SR>,
  minColumnWidth: number,
): number {
  if (key === SELECT_COLUMN_KEY) {
    return width;
  }
  width = Math.max(width, minWidth ?? minColumnWidth);

  if (typeof maxWidth === 'number') {
    return Math.min(width, maxWidth);
  }

  return width;
}
